import { getFirestore, doc, getDoc, collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { app } from "../firebase-config.js";
import { PaymentConfig } from "../config/payment.config.js";
import { APP_VERSION } from "../version.js";
import { locale } from "../services/LocaleService.js";

const db = getFirestore(app);
const auth = getAuth(app); // Initialize Auth once

// [CONFIGURATION] Hardcoded Product List (Fallback / Initial State)
const DEFAULT_PRODUCTS = [
    { "id": "stardust", "order_index": 1, "name": "Stardust", "sub_name": "스타 더스트", "price": 1000, "amount": 1000, "bonus_rate": 0, "theme_color": "gray", "badge_text": "", "is_active": true },
    { "id": "shooting_star", "order_index": 2, "name": "Shooting Star", "sub_name": "슈팅 스타", "price": 5000, "amount": 5100, "bonus_rate": 2, "theme_color": "blue", "badge_text": "", "is_active": true },
    { "id": "little_bear", "order_index": 3, "name": "Little Bear", "sub_name": "리틀 베어", "price": 10000, "amount": 10500, "bonus_rate": 5, "theme_color": "purple", "badge_text": "", "is_active": true },
    { "id": "silver_river", "order_index": 4, "name": "Silver River", "sub_name": "실버 리버", "price": 30000, "amount": 32400, "bonus_rate": 8, "theme_color": "silver", "badge_text": "", "is_active": true },
    { "id": "galaxy_master", "order_index": 5, "name": "Galaxy Master", "sub_name": "갤럭시 마스터", "price": 50000, "amount": 56000, "bonus_rate": 12, "theme_color": "gold", "badge_text": "", "is_active": true },
    { "id": "star_maker", "order_index": 6, "name": "Star Maker", "sub_name": "스타 메이커 (VVIP)", "price": 100000, "amount": 115000, "bonus_rate": 15, "theme_color": "red", "badge_text": "BEST", "is_active": true }
];

let isProcessing = false; // Prevent double clicks

export const StarChargePage = async (container) => {
    // 0. Render Initial Layout (With Defaults)
    renderLayout(container, DEFAULT_PRODUCTS);

    // 1. Setup Real-time Listener for Dynamic Products
    const unsubProduct = onSnapshot(query(collection(db, "star_products"), orderBy("order_index", "asc")), (snapshot) => {
        const dynamicProducts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        updateProductGrid(container, dynamicProducts);
    });

    // State for PG Config
    const configState = {
        pg_mode: 'TEST',
        pg_client_key: null
    };

    // 1-1. Setup Real-time Listener for Global Economy
    const unsubEconomy = onSnapshot(doc(db, "settings", "global_economy"), (docSnap) => {
        if (docSnap.exists()) {
            const data = docSnap.data();
            updatePriceTable(container, data.prices || data);

            // [Economy Update] Sync PG Settings
            configState.pg_mode = data.pg_mode || 'TEST';
            configState.pg_client_key = data.pg_client_key || null;
            // Note: Service init will happen just-in-time before payment to ensure latest key
        }
    });

    // Cleanup Listener
    container.destroy = () => {
        console.log("[StarChargePage] Cleaning up listeners...");
        unsubProduct();
        unsubEconomy();
        if (container._authUnsub) container._authUnsub();
        if (container._userUnsub) container._userUnsub();
    };

    // 2. Logic & Event Binding
    await bindPageEvents(container, configState);
};

// --- Rendering Helpers ---

const renderLayout = (container, products) => {
    container.innerHTML = `
        <div class="min-h-screen font-sans relative text-gray-100 overflow-x-hidden bg-gray-900">
            <!-- Background Image -->
            <div class="fixed inset-0 z-0">
                <img src="/login.webp" class="w-full h-full object-cover opacity-60" alt="Background" />
                <div class="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-gray-900/90 to-gray-900"></div>
                <div class="absolute inset-0 bg-black/20 backdrop-blur-[2px]"></div>
            </div>

            <!-- Content Wrapper -->
            <div class="relative z-10 max-w-7xl mx-auto px-6 py-6 flex flex-col min-h-screen">
                
                <!-- 1. Header (Back, Title, Stats) -->
                <div class="flex flex-col md:flex-row justify-between items-center mb-10 animate-fade-in-down">
                    <div class="flex items-center gap-4 mb-4 md:mb-0 w-full md:w-auto">
                        <button onclick="history.back()" class="group p-2 -ml-2 text-gray-400 hover:text-white transition-colors rounded-full hover:bg-white/10">
                            <span class="material-symbols-outlined group-hover:-translate-x-1 transition-transform">arrow_back</span>
                        </button>
                        <div class="flex items-center gap-2">
                             <span class="material-symbols-outlined text-yellow-500 fill-current text-2xl">stars</span>
                             <h1 class="text-xl font-bold text-yellow-500 tracking-wide">${locale.t('star_station.title')}</h1>
                        </div>
                    </div>

                    <!-- User Stats -->
                    <div class="flex items-center gap-6 bg-black/40 px-6 py-3 rounded-xl border border-white/5 backdrop-blur-md shadow-xl">
                        <!-- Activity Points -->
                        <div class="flex flex-col items-end">
                             <span class="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Activity Point</span>
                             <span class="font-mono font-bold text-green-400 text-lg" id="val-ap">Loading...</span>
                        </div>
                        <div class="w-px h-8 bg-white/10"></div>
                        <!-- Star Points -->
                        <div class="flex flex-col items-end">
                             <span class="text-[10px] text-gray-400 font-bold uppercase tracking-wider">${locale.t('star_station.my_stars')}</span>
                             <span class="font-mono font-bold text-yellow-400 text-lg" id="val-star">Loading...</span>
                        </div>
                    </div>
                </div>

                <!-- 2. Hero Section -->
                <div class="text-center mb-16 animate-fade-in-up">
                    <h2 class="text-4xl md:text-5xl font-black text-white mb-3 tracking-tight drop-shadow-2xl">Premium Star Charging</h2>
                    <p class="text-gray-400 text-sm md:text-base font-medium mb-6">${locale.t('star_station.subtitle')}</p>
                    
                    <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-900/30 border border-emerald-500/30 text-emerald-400 text-xs font-bold shadow-lg shadow-emerald-900/20 hover:bg-emerald-900/40 transition-colors cursor-help" title="Secure Payment">
                        <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                        Secure Payment by Toss Payments
                    </div>
                </div>

                <!-- 3. Economy Monitor (Price Ticker) -->
                <div class="max-w-5xl mx-auto w-full mb-16 animate-fade-in relative z-20">
                     <div class="bg-white/5 border border-white/10 rounded-2xl p-1 backdrop-blur-md shadow-2xl flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-white/10">
                        
                        <!-- Header -->
                        <div class="p-4 flex flex-col justify-center items-center md:items-start min-w-[140px]">
                            <span class="text-xs font-bold text-gray-500 uppercase">Live Rates</span>
                            <span class="text-[10px] text-gray-600 font-mono tracking-widest mt-0.5">LIVE ECONOMY</span>
                        </div>

                        <!-- Ticker Items -->
                        <div id="price-ticker-container" class="flex-1 grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/5">
                             <div class="flex items-center justify-center p-4 text-gray-500 italic text-sm">Loading market data...</div>
                        </div>

                     </div>
                </div>

                <!-- 4. Product Grid -->
                <div class="max-w-6xl mx-auto w-full mb-20 relative z-20">
                     <div id="product-grid" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 auto-rows-fr">
                        <!-- Products -->
                     </div>
                </div>

                <!-- Footer -->
                 <div class="max-w-4xl mx-auto px-6 text-center pb-12 border-t border-white/5 pt-8 text-gray-600">
                     <div class="text-xs space-y-1">
                        <p class="font-bold text-gray-500">Refund Policy</p>
                        <p>Star points are non-refundable after use. Unused points can be refunded within 7 days of purchase.</p>
                        <p class="mt-4 text-[10px] font-mono opacity-50">APP VER: ${APP_VERSION}</p>
                    </div>
                </div>

            </div>
        </div>
    `;

    updateProductGrid(container, products);
};

const updatePriceTable = (container, economyData) => {
    const el = container.querySelector('#price-ticker-container');
    if (!el || !economyData) return;

    // Helper safely get prop with Fallback (Standard keys || Legacy keys)
    const getPrice = (type, mode) => {
        // 1. Try Standard (webnovel, webtoon)
        let val = economyData[type]?.[mode];
        if (val !== undefined) return val;

        // 2. Try Legacy (novel, toon) - for backward compatibility
        const legacyMap = { 'webnovel': 'novel', 'webtoon': 'toon', 'davinci': 'cine' };
        const legacyType = legacyMap[type];
        if (legacyType) {
            val = economyData[legacyType]?.[mode];
            if (val !== undefined) return val;
        }

        return '-';
    };

    el.innerHTML = `
        <div class="flex flex-col items-center justify-center p-3 text-center">
            <span class="font-bold text-gray-200">${locale.t('menu.webnovel')}</span>
            <span class="text-[10px] text-gray-400 mt-1">Rent <strong class="text-yellow-400">${getPrice('webnovel', 'rent_price')}S</strong> / Own <strong class="text-yellow-400">${getPrice('webnovel', 'own_price')}S</strong></span>
        </div>
        <div class="flex flex-col items-center justify-center p-3 text-center">
             <span class="font-bold text-gray-200">${locale.t('menu.webtoon')}</span>
             <span class="text-[10px] text-gray-400 mt-1">Rent <strong class="text-yellow-400">${getPrice('webtoon', 'rent_price')}S</strong> / Own <strong class="text-yellow-400">${getPrice('webtoon', 'own_price')}S</strong></span>
        </div>
        <div class="flex flex-col items-center justify-center p-3 text-center">
             <span class="font-bold text-gray-200">${locale.t('menu.davinci')}</span>
             <span class="text-[10px] text-gray-400 mt-1">Rent <strong class="text-yellow-400">${getPrice('davinci', 'rent_price')}S</strong> / Own <strong class="text-yellow-400">${getPrice('davinci', 'own_price')}S</strong></span>
        </div>
    `;
};

const updateProductGrid = (container, products) => {
    const grid = container.querySelector('#product-grid');
    if (!grid) return;

    const now = new Date();

    // Filter Logic: Active AND (within period OR no period)
    const activeProducts = products.filter(p => {
        if (p.is_active === false) return false;

        // Time Limit Check
        // Safe check: invalid dates will result in NaN, which makes comparisons false
        if (p.promotion_start) {
            const start = new Date(p.promotion_start);
            if (!isNaN(start) && start > now) return false;
        }
        if (p.promotion_end) {
            const end = new Date(p.promotion_end);
            if (!isNaN(end) && end < now) return false;
        }

        return true;
    });

    if (activeProducts.length === 0) {
        grid.innerHTML = `
            <div class="col-span-full py-20 flex flex-col items-center justify-center text-gray-500 space-y-2">
                 <span class="material-symbols-outlined text-4xl text-gray-700">storefront</span>
                 <p>No products available.</p>
            </div>
        `;
        return;
    }

    grid.innerHTML = activeProducts.map(p => {
        // STYLE LOGIC based on tier/theme (Dark Mode Adapted)
        const isBest = p.badge_text === 'BEST' || p.theme_color === 'red' || p.theme_color === 'gold';

        // Base Card Style
        const baseClass = "bg-[#1e1e24] border border-white/5 rounded-2xl relative flex flex-col btn-purchase cursor-pointer transition-transform transition-shadow duration-200 group";

        const btnStyle = isBest
            ? 'bg-gradient-to-r from-yellow-500 to-amber-600 text-black shadow-lg hover:shadow-yellow-500/40 border-transparent font-black'
            : 'bg-white/5 text-gray-200 border border-white/10 hover:bg-white/10 hover:border-white/20';

        // Hover Effect
        const hoverClass = isBest
            ? "hover:-translate-y-1 hover:shadow-2xl hover:shadow-yellow-500/10 hover:border-yellow-500/30"
            : "hover:-translate-y-1 hover:shadow-xl hover:shadow-black/50 hover:border-indigo-500/30";

        const priceColor = isBest
            ? "text-transparent bg-clip-text bg-gradient-to-br from-yellow-300 to-amber-500"
            : "text-white";

        const bonusPercent = p.bonus_rate || 0;

        // Localize Product Name if possible (or use default from DB)
        // Hardcoded mapping for demo:
        const localName = locale.t(`star_station.product_names.${p.id.toLowerCase()}`) || p.sub_name || p.name;


        return `
        <div class="${baseClass} ${hoverClass} overflow-hidden" data-id="${p.id}" data-price="${p.price}">
            
            <!-- Badges -->
            ${p.badge_text ? `
                <div class="absolute top-0 right-0">
                    <div class="bg-gradient-to-bl from-red-600 to-red-700 text-white text-[10px] font-black px-3 py-1.5 rounded-bl-xl shadow-lg z-10">
                        ${p.badge_text}
                    </div>
                </div>
            ` : ''}

            <!-- Glow Effect -->
            ${isBest ? '<div class="absolute -inset-1 bg-gradient-to-r from-yellow-500/20 to-amber-600/20 blur opacity-0 group-hover:opacity-100 transition duration-500"></div>' : ''}

            <div class="p-6 flex-1 flex flex-col relative z-10">
                <!-- Title Section -->
                <div class="mb-4">
                    <h3 class="text-xl font-bold text-gray-100 tracking-tight group-hover:text-white transition-colors">${p.name}</h3>
                    <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mt-1">${localName}</p>
                </div>

                <!-- Price & Amount -->
                <div class="flex items-baseline gap-1 mb-6">
                     <span class="text-4xl font-black ${priceColor}">${p.amount.toLocaleString()}</span>
                     <span class="text-sm font-bold text-gray-500">Stars</span>
                </div>

                <!-- Benefits List (Corrected & Localized) -->
                <ul class="text-sm text-gray-400 space-y-3 mb-8 flex-1">
                    <li class="flex items-start gap-2">
                        <span class="material-symbols-outlined text-gray-600 text-lg">check_circle</span>
                        <span>
                            <strong class="text-gray-300">₩${p.price.toLocaleString()}</strong> (VAT Excl.)
                        </span>
                    </li>
                     ${bonusPercent > 0 ? `
                    <li class="flex items-start gap-2">
                        <span class="material-symbols-outlined text-yellow-500 text-lg">hotel_class</span>
                        <span class="font-bold text-yellow-500">+${bonusPercent}% Bonus</span>
                    </li>
                    ` : ''}
                </ul>

                <!-- CTA Button -->
                <button class="w-full py-3.5 rounded-xl text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-all ${btnStyle}">
                    <span>${locale.t('star_station.btn_charge')}</span>
                    ${isBest ? '<span class="material-symbols-outlined text-sm">bolt</span>' : ''}
                </button>
            </div>
            
        </div>
        `;
    }).join('');

    // Re-bind click events for dynamic content
    const btns = grid.querySelectorAll('.btn-purchase');
    btns.forEach(btn => {
        btn.onclick = (e) => {
            if (isProcessing) return; // Debounce
            const targetId = btn.getAttribute('data-id');
            const product = activeProducts.find(p => p.id === targetId);
            handlePurchaseCheck(product);
        };
    });
};


const bindPageEvents = async (container, configState) => {
    try {
        // [Auth & Data] Real-time Sync for Header Points
        container._authUnsub = onAuthStateChanged(auth, (user) => {
            if (container._userUnsub) {
                container._userUnsub(); // Clean previous user listener if any
                container._userUnsub = null;
            }

            if (user) {
                // Listen for User Data Changes (Points)
                container._userUnsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
                    if (snap.exists()) {
                        const data = snap.data();
                        const bal = data.starPoints || 0;
                        const ap = data.activityPoints || 0;

                        const elBal = container.querySelector('#val-star');
                        if (elBal) elBal.textContent = bal.toLocaleString();

                        const elAp = container.querySelector('#val-ap');
                        if (elAp) elAp.textContent = ap.toLocaleString() + " P";
                    }
                });
            } else {
                // Reset to default
                const elBal = container.querySelector('#val-star');
                if (elBal) elBal.textContent = 'Login Required';

                const elAp = container.querySelector('#val-ap');
                if (elAp) elAp.textContent = '-';
            }
        });

        // [Bind] Back
        // [Bind] Back - Handled inline (history.back())
        // container.querySelector('#btn-back-wallet') ...

        // [Handle] Payment Callbacks
        handlePaymentCallback();

        // Pass configState to button handlers closure or global scope?
        // Better to attach it to container or pass via argument in handlePurchaseCheck
        container._pgConfig = configState;

    } catch (e) {
        console.error("StarChargePage Bind Error:", e);
    }
};

// --- Logic ---

const handlePurchaseCheck = async (product) => {
    if (!product || isProcessing) return;
    isProcessing = true; // Lock

    try {
        const user = auth.currentUser;

        // Access container state (Hack: assuming we can find the container, or pass it)
        // Since we are inside a module, we don't have direct access to 'container' instance here easily 
        // unless we passed it to 'updateProductGrid' -> 'click'.
        // FIX: The click listener was defined in 'updateProductGrid'. 
        // We need to re-architect slightly or fetch fresh config.
        // Fetching fresh config is safer for payments.

        // [Check] Login Guard
        if (!user) {
            const confirmed = await window.Modal.confirm(
                locale.t('msg.login_required') || "Log in to continue.",
                "Login Required"
            );

            if (confirmed) {
                window.location.hash = "#/login";
            }
            isProcessing = false; // Unlock
            return;
        }

        await handlePurchase(user, product);
    } catch (e) {
        console.error(e);
        isProcessing = false;
    }
};

async function handlePurchase(user, product) {
    try {
        // [1] Fetch Global Economy Settings (Fresh Check)
        // Even though we have a listener, for payments it's good to 'Get Latest' to avoid stale closure state.
        const settingsRef = doc(db, "settings", "global_economy");
        const settingsSnap = await getDoc(settingsRef);
        // Default to TEST if missing
        const settings = settingsSnap.exists() ? settingsSnap.data() : { pg_mode: 'TEST' };

        const mode = settings.pg_mode || 'TEST';
        // USE KEY from DB (Live) or Default (Test)
        // If 'TEST' mode, we can use the hardcoded test key from Config or DB if present.
        // If 'LIVE' mode, we MUST use DB key.

        let clientKey;

        if (mode === 'LIVE') {
            // LIVE: MUST use DB key
            clientKey = settings.pg_client_key;
        } else {
            // TEST: ALWAYS use hardcoded test key (prevents Firestore stale key mismatch)
            clientKey = "test_ck_ORzdMaqN3wP69dp6QpagV5AkYXQG";
        }

        if (!clientKey) {
            throw new Error(`Payment Configuration Error: Missing Client Key for ${mode} mode.`);
        }

        console.log(`[Payment] Initializing Toss in ${mode} Mode for ${product.name}`);

        // [2] Initialize SDK
        const tossPayments = window.TossPayments(clientKey);
        const orderId = `order_${user.uid.slice(0, 5)}_${Date.now()}`;

        // [2.5] Save product context for callback (Toss redirect loses JS state)
        sessionStorage.setItem('pending_star_payment', JSON.stringify({
            productId: product.id,
            orderId: orderId
        }));

        // [3] Request Payment
        await tossPayments.requestPayment('카드', {
            amount: product.price,
            orderId: orderId,
            orderName: `Star Charge: ${product.name}`,
            customerName: user.displayName || "SSULWHA Member",
            successUrl: window.location.href, // [FIX] Use exact current URL (Hash included)
            failUrl: window.location.href,    // [FIX] Use exact current URL (Hash included)
        });

    } catch (error) {
        isProcessing = false; // Unlock

        // Suppress Cancellation Errors
        // Toss Payments often returns 'USER_CANCEL' code or '취소되었습니다' message
        if (error.code === 'USER_CANCEL' || error.message.includes('취소')) {
            console.log("Payment cancelled by user.");
            return;
        }

        console.error("Toss Request Error:", error);
        window.Modal.alert(locale.t('payment.error') || error.message);
    }
}

async function handlePaymentCallback() {
    // [FIX] Hash Router Compatibility
    // Toss attaches Query Params AFTER the Hash in some cases, or BEFORE.
    // We check both: window.location.search (Standard) AND split from hash.

    const standardParams = new URLSearchParams(window.location.search);
    const hashParts = window.location.hash.split('?');
    const hashParams = hashParts.length > 1 ? new URLSearchParams(hashParts[1]) : new URLSearchParams();

    // Helper to get param from either source
    const getParam = (key) => standardParams.get(key) || hashParams.get(key);

    const paymentKey = getParam("paymentKey");
    const orderId = getParam("orderId");
    const amount = getParam("amount");

    // Success Case
    if (paymentKey && orderId && amount) {
        // [CRITICAL FIX] Clear ALL query strings (search AND hash) to prevent re-execution on refresh
        const cleanUrl = window.location.origin + window.location.pathname + window.location.hash.split('?')[0];
        window.history.replaceState({}, document.title, cleanUrl);

        // [FIX] Restore product context saved before Toss redirect
        const pendingRaw = sessionStorage.getItem('pending_star_payment');
        const pending = pendingRaw ? JSON.parse(pendingRaw) : {};
        sessionStorage.removeItem('pending_star_payment');

        // [GUARD] If productId is missing, the payment context was lost (stale refresh)
        if (!pending.productId) {
            console.warn('[Payment] No productId found in sessionStorage. Skipping stale callback.');
            return;
        }

        await verifyPaymentOnServer(paymentKey, orderId, parseInt(amount), pending.productId);
        return;
    }

    // Failure Case
    const failCode = getParam("code");
    const failMessage = getParam("message");

    if (failCode) {
        // [CRITICAL FIX] Clear ALL query strings (search AND hash)
        const cleanUrl = window.location.origin + window.location.pathname + window.location.hash.split('?')[0];
        window.history.replaceState({}, document.title, cleanUrl);

        // [UX] Silently ignore user cancellations
        if (failCode === 'USER_CANCEL' || failMessage?.includes('취소')) {
            console.log("[Payment] User cancelled.");
            window.Modal.toast(locale.t('payment.user_cancel'));
        } else {
            // Actual Error
            console.error(`[Payment] Failed: ${failCode} - ${failMessage}`);
            await window.Modal.alert(`${locale.t('payment.error')}: ${failMessage}`);
        }
    }
}

async function verifyPaymentOnServer(paymentKey, orderId, amount, productId) {
    const overlay = document.createElement('div');
    overlay.className = "fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm";
    overlay.innerHTML = `
        <div class="bg-white p-6 rounded-2xl shadow-2xl flex flex-col items-center gap-4 animate-bounce-in">
            <span class="loader-spin w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></span>
            <div class="text-center">
                <h3 class="font-bold text-gray-900 text-lg">Verifying Payment</h3>
                <p class="text-sm text-gray-500">Securing your stars...</p>
            </div>
        </div>
    `;
    document.body.appendChild(overlay);

    try {
        const functions = getFunctions(app, 'us-central1');
        const confirmPaymentFn = httpsCallable(functions, 'confirmPayment');

        const result = await confirmPaymentFn({ paymentKey, orderId, amount, productId });

        document.body.removeChild(overlay);

        if (result.data.success) {
            await window.Modal.alert(
                locale.t('payment.verify_success', { amount: result.data.starAmount })
            );
            window.location.hash = "#/mypage?tab=wallet";
        } else {
            throw new Error(result.data.message || "Verification Failed");
        }

    } catch (error) {
        if (document.body.contains(overlay)) document.body.removeChild(overlay);
        console.error("Payment Verification Error:", error);
        await window.Modal.alert(`${locale.t('payment.error')}: ${error.message}`);
    }
}
